import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { SidebarContext, type SidebarContextValue } from "./sidebar-context-core";

/**
 * Estado UI del shell del dashboard:
 *   - `collapsed`: en desktop (lg+), si el sidebar está en modo icono.
 *   - `mobileOpen`: en <lg, si el sheet del sidebar está abierto.
 *
 * `collapsed` se persiste en localStorage. `mobileOpen` es efímero —
 * siempre arranca cerrado al navegar / recargar.
 *
 * El `useSidebar` hook vive en `sidebar-context-core.ts` para que este
 * archivo solo exporte componentes (React Fast Refresh friendly).
 */

const STORAGE_KEY = "arya-backoffice-sidebar-collapsed";

function loadCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState<boolean>(loadCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* no-op */
    }
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => setCollapsed((v) => !v), []);
  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const value = useMemo<SidebarContextValue>(
    () => ({
      collapsed,
      setCollapsed: setCollapsed as Dispatch<SetStateAction<boolean>>,
      toggleCollapsed,
      mobileOpen,
      setMobileOpen: setMobileOpen as Dispatch<SetStateAction<boolean>>,
      openMobile,
      closeMobile,
    }),
    [collapsed, mobileOpen, toggleCollapsed, openMobile, closeMobile],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}
